import { useCallback, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import Cropper, { type Area } from "react-easy-crop";
import { Button } from "@/components/ui/button";

interface AvatarCropperProps {
  imageSrc: string; // base64 data URL or object URL
  onComplete: (croppedBlob: Blob) => void;
  onCancel: () => void;
  uploading?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Draws the file to a canvas respecting EXIF orientation, returns a data URL.
 * Prevents the "photo rotated 90°" issue on Android/iOS camera captures.
 */
export async function applyExifOrientation(file: File): Promise<string> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0);
    bitmap.close();
    return canvas.toDataURL("image/jpeg", 0.92);
  } catch {
    // Browser can't decode (HEIC on Android, older Safari, unsupported formats).
    // Fall back to raw data URL — rotation may be wrong but the user can still crop.
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("File read failed"));
      reader.readAsDataURL(file);
    });
  }
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    image.crossOrigin = "anonymous";
    image.src = url;
  });
}

/**
 * Extracts the cropped region from the original image and outputs
 * a 400x400 JPEG blob at 0.85 quality (~30-60KB typical).
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = 400;
  canvas.height = 400;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context unavailable");

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    400,
    400,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Blob creation failed"));
      },
      "image/jpeg",
      0.85,
    );
  });
}

// ── Component ────────────────────────────────────────────────────────────────

const AvatarCropper = ({
  imageSrc,
  onComplete,
  onCancel,
  uploading,
}: AvatarCropperProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const handleCropComplete = useCallback(
    (_croppedArea: Area, pixelsArea: Area) => {
      setCroppedAreaPixels(pixelsArea);
    },
    [],
  );

  const handleUsePhoto = async () => {
    if (!croppedAreaPixels) return;
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onComplete(blob);
    } catch (err) {
      console.error("[AvatarCropper]", err);
    }
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(v) => { if (!v && !uploading) onCancel(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          className="fixed inset-0 z-[200] flex flex-col focus:outline-none"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.92)" }}
          onEscapeKeyDown={(e) => { if (uploading) e.preventDefault(); }}
        >
          <DialogPrimitive.Title className="sr-only">Crop photo</DialogPrimitive.Title>

          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={uploading}
              className="text-sm font-medium text-white/80 hover:text-white transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <Button
              type="button"
              onClick={handleUsePhoto}
              disabled={uploading || !croppedAreaPixels}
              size="sm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {uploading ? "Uploading…" : "Use photo"}
            </Button>
          </div>

          {/* Cropper */}
          <div className="relative flex-1">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
              minZoom={1}
              maxZoom={3}
            />
          </div>

          {/* Zoom slider */}
          <div className="px-8 py-6">
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/60">−</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                disabled={uploading}
                className="flex-1 h-1 rounded-full accent-primary"
                style={{ accentColor: "#7A9A7C" }}
              />
              <span className="text-xs text-white/60">+</span>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default AvatarCropper;
